import type { User } from '@supabase/supabase-js'
import i18n from '../i18n/index'
import { clientLanguageGuess } from './clientContext'
import {
  readStoredLanguagePreference,
  resolveAnonymousLanguage,
  writeStoredLanguagePreference,
} from './storedLanguage'
import { DEFAULT_LANGUAGE, normalizeLanguageCode, type AppLanguage } from '../i18n/languages'
import { supabase } from './supabaseClient'

export {
  readStoredLanguagePreference,
  resolveAnonymousLanguage,
  writeStoredLanguagePreference,
} from './storedLanguage'

export async function applyLanguage(language: AppLanguage): Promise<void> {
  const resolved = normalizeLanguageCode(language)
  if (i18n.language !== resolved) {
    await i18n.changeLanguage(resolved)
  }
  document.documentElement.lang = resolved
  writeStoredLanguagePreference(resolved)
}

export async function loadProfileLanguage(userId: string): Promise<AppLanguage | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('language_preference')
    .eq('id', userId)
    .maybeSingle()

  if (!error && data?.language_preference) {
    return normalizeLanguageCode(String(data.language_preference))
  }

  const { data: prefs, error: prefsError } = await supabase
    .from('user_preferences')
    .select('language')
    .eq('user_id', userId)
    .maybeSingle()

  if (!prefsError && prefs?.language) {
    return normalizeLanguageCode(String(prefs.language))
  }

  return null
}

export async function saveProfileLanguage(userId: string, language: AppLanguage): Promise<string | null> {
  const resolved = normalizeLanguageCode(language)
  const { error } = await supabase
    .from('profiles')
    .update({ language_preference: resolved })
    .eq('id', userId)

  if (error) return error.message

  await supabase
    .from('user_preferences')
    .upsert({ user_id: userId, language: resolved }, { onConflict: 'user_id' })

  await applyLanguage(resolved)

  const { error: cacheError } = await supabase.functions.invoke('invalidate-language-cache')
  if (cacheError) {
    console.warn('Language cache invalidation failed:', cacheError.message)
  }

  return null
}

function languageFromUserMetadata(user: Pick<User, 'user_metadata'>): AppLanguage | null {
  const raw = user.user_metadata?.language_preference
  if (typeof raw !== 'string' || !raw.trim()) return null
  return normalizeLanguageCode(raw)
}

/** Load profile language, or seed it from signup metadata / local picker choice. */
export async function syncLanguageForUser(user: Pick<User, 'id' | 'user_metadata'>): Promise<AppLanguage> {
  const profileLanguage = await loadProfileLanguage(user.id)
  if (profileLanguage) {
    await applyLanguage(profileLanguage)
    return profileLanguage
  }

  const resolved = languageFromUserMetadata(user)
    ?? readStoredLanguagePreference()
    ?? clientLanguageGuess()

  const saveError = await saveProfileLanguage(user.id, resolved)
  if (saveError) {
    console.warn('Could not persist language preference:', saveError)
    await applyLanguage(resolved)
  }

  return resolved
}

export async function resolveInitialLanguage(userId?: string | null): Promise<AppLanguage> {
  if (userId) {
    const profileLanguage = await loadProfileLanguage(userId)
    if (profileLanguage) return profileLanguage
  }

  return resolveAnonymousLanguage()
}

export function authSignupLanguageMetadata(language: AppLanguage): { language_preference: AppLanguage } {
  return { language_preference: normalizeLanguageCode(language) }
}

export function fallbackAuthEmailLanguage(): AppLanguage {
  return DEFAULT_LANGUAGE
}
